/**
 * Centralized error message extraction for the entire app.
 *
 * Handles every shape an axios / fetch error can take:
 *   • Pydantic validation arrays  → "password: must contain a number"
 *     (note: lib/api.js already flattens these, but we re-handle here too
 *     so non-axios callers and future SDKs are safe by default)
 *   • Plain {detail: "string"}    → "string"
 *   • Plain {message: "string"}   → "string"
 *   • Network failure (no response, ECONNABORTED) → "Network error — check your connection."
 *   • Timeout (ETIMEDOUT)         → "Request timed out — please try again."
 *   • Unknown error / undefined   → fallback string
 *
 * Use everywhere you would have written `err.response?.data?.detail`:
 *
 *     import { errorMessage } from "@/lib/errors";
 *     toast.error(errorMessage(err, "Could not save"));
 */

function flattenPydantic(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (!first) return null;
    if (typeof first === "string") return first;
    const field = Array.isArray(first.loc)
      ? first.loc.filter((p) => p !== "body" && p !== "query").join(".")
      : "";
    const msg = first.msg || "invalid";
    return field ? `${field}: ${msg}` : msg;
  }
  // Single Pydantic-shaped object (uncommon but possible from custom handlers)
  if (detail && typeof detail === "object" && typeof detail.msg === "string") {
    return detail.msg;
  }
  return null;
}

/**
 * Fire-and-forget crash reporter. Posts the error context to
 * /api/client/log so the operator can review in Admin → Client Errors.
 *
 * Never throws, never awaits — safe to call from anywhere (interceptors,
 * ErrorBoundary, ad-hoc try/catch).
 *
 * Severity levels: info | warning | error | critical
 */
let _lastReportKey = "";
let _lastReportAt = 0;
export function reportError(err, { severity = "error", component = "", extra = null } = {}) {
  try {
    const message = errorMessage(err, "Unknown error");
    const stack = (err && (err.stack || (err.response?.data && JSON.stringify(err.response.data).slice(0, 1000)))) || "";
    // De-dupe within 4 seconds so a single failure burst doesn't fill the
    // collection — keyed by message+component.
    const key = `${component}|${message}`;
    const now = Date.now();
    if (key === _lastReportKey && now - _lastReportAt < 4000) return;
    _lastReportKey = key;
    _lastReportAt = now;

    const payload = {
      error_message: String(message).slice(0, 1000),
      stack: String(stack).slice(0, 8000),
      url: typeof window !== "undefined" ? window.location.href : "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      severity,
      component,
      extra: extra && typeof extra === "object" ? extra : null,
    };
    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const token = (typeof localStorage !== "undefined") ? localStorage.getItem("auth_token") : null;
    const headers = { "Content-Type": "application/json" };
    if (token && token !== "pending") headers.Authorization = `Bearer ${token}`;
    // Use fetch directly (no axios) so the interceptor doesn't recurse on
    // a logging-endpoint failure. keepalive lets the request fire even
    // during a hard navigation (e.g. ErrorBoundary→reload).
    fetch(`${baseUrl}/api/client/log`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {/* swallow */});
  } catch {
    /* never break the app over a logging failure */
  }
}

export function errorMessage(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;

  // Network-level failures (no response object)
  if (err.code === "ERR_NETWORK") return "Network error — check your connection.";
  if (err.code === "ECONNABORTED" || err.code === "ETIMEDOUT") {
    return "Request timed out — please try again.";
  }

  const data = err.response?.data;
  if (data) {
    const fromDetail = flattenPydantic(data.detail);
    if (fromDetail) return fromDetail;
    if (typeof data.message === "string") return data.message;
    if (typeof data === "string") return data;
  }

  // Server returned without a body — surface HTTP status
  if (err.response?.status) {
    const map = {
      400: "Invalid request — please check the form.",
      401: "Please sign in to continue.",
      403: "You don't have permission to do this.",
      404: "Not found.",
      409: "That conflicts with an existing record.",
      413: "File is too large.",
      422: "We couldn't process that — please review the form.",
      429: "Too many requests — slow down for a moment.",
      500: "Server error. Our team has been notified.",
      502: "The service is briefly unavailable.",
      503: "The service is briefly unavailable.",
    };
    return map[err.response.status] || `Request failed (HTTP ${err.response.status}).`;
  }

  // Last resort
  if (typeof err.message === "string") return err.message;
  return fallback;
}

export default errorMessage;
